import { Auth } from 'aws-amplify'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import ReactCodeInput from 'react-code-input'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import {
  classNames,
  EMAIL_REGEX,
  notifyBugsnag,
  trackAdsConversion
} from '../helpers'
import { trackEvent } from '../helpers/analytics'
import { isCompanyEmail } from '../helpers/companyEmailValidator'
import { ONBOARDING_SKIP_ROUTE, skipsOnboarding } from '../helpers/experiment'
import { localStorage } from '../helpers/local-storage'
import Alert from './alert'
import Button from './button'
import InputFormik from './input-formik'

const AuthContainer = ({
  context,
  signupEmail,
  fixedHeight,
  hideEmailField,
  onLoadingChange
}) => {
  const { t, i18n } = useTranslation()

  const [state, setState] = useState('ENTER_EMAIL')

  const [startTimer, setStartTimer] = useState(false)
  const [error, setError] = useState(null)
  const [cognitoUser, setCognitoUser] = useState(null)

  // Wait for aivy_form_id when signup email is prefilled (DISCOVER flow)
  const [aivyFormIdReady, setAivyFormIdReady] = useState(false)

  const isSignupWithPrefilledEmail = useMemo(() => {
    if (context !== 'SIGNUP') return false
    try {
      return !!new URLSearchParams(window.location.search).get('email')
    } catch {
      return false
    }
  }, [context])

  // Email aus URL-Query (z.B. /login?email=...) — wird vom Funnel-Welcome-Mail
  // Link mitgegeben damit User nur den OTP-Code eingeben muss.
  const urlEmail = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('email') || ''
    } catch {
      return ''
    }
  }, [])

  // ?autologin=1 — vom Funnel-Done-Screen mitgegeben. Triggert nach Mount
  // automatisch confirmEmail (Auth.signIn) sodass der OTP-Code direkt
  // verschickt wird und User ohne Klick im Code-Entry landet.
  const wantsAutoLogin = useMemo(() => {
    try {
      const v = new URLSearchParams(window.location.search).get('autologin')
      return v === '1' || v === 'true'
    } catch {
      return false
    }
  }, [])
  const autoLoginFiredRef = useRef(false)
  const codeInputRef = useRef(null)

  useEffect(() => {
    if (state === 'ENTER_CODE' && codeInputRef.current) {
      try {
        codeInputRef.current.textInput?.[0]?.focus()
      } catch {}
    }
  }, [state])

  const forms = useMemo(() => {
    return {
      email: {
        id: 'email',
        type: 'email',
        label: t('authenticator.email_label_' + context.toLowerCase()),
        placeholder: t(
          'authenticator.email_placeholder_' + context.toLowerCase()
        ),
        autoComplete: 'email'
      },
      code: {
        id: 'code',
        // type: 'number',
        label: t('authenticator.code_label_' + context.toLowerCase()),
        description: t(
          'authenticator.code_description_' + context.toLowerCase()
        ),
        placeholder: t(
          'authenticator.code_placeholder_' + context.toLowerCase()
        ),
        autoComplete: 'one-time-code'
      }
    }
  }, [context, t])

  const validationSchema = useMemo(() => {
    if (state === 'ENTER_EMAIL') {
      return Yup.object({
        email: Yup.string()
          .matches(
            EMAIL_REGEX,
            t('authenticator.validation_email_' + context.toLowerCase())
          )
          .test(
            'company-email',
            t('authenticator.validation_email_signup'),
            (value) => {
              if (context !== 'SIGNUP') return true
              if (!value) return true
              if (!value.includes('@')) return true
              return isCompanyEmail(value)
            }
          )
          .required(
            t('authenticator.validation_required_' + context.toLowerCase())
          )
      })
    }

    if (state === 'ENTER_CODE') {
      return Yup.object({
        code: Yup.string().required(
          t('authenticator.validation_required_' + context.toLowerCase())
        )
      })
    }
  }, [context, state, t])

  const formik = useFormik({
    validateOnBlur: false,
    validateOnMount: false,
    initialValues: {
      email:
        urlEmail || localStorage.getItem('signup_email') || signupEmail || '',
      code: ''
    },
    validationSchema
  })

  useEffect(() => {
    if (startTimer) {
      const timer = setTimeout(() => setState('CODE_EXPIRED'), 1000 * 60 * 14.8)
      return () => clearTimeout(timer)
    }
    if (state === 'CODE_EXPIRED') {
      setStartTimer(false)
    }
  }, [startTimer, state])

  useEffect(() => {
    setError(null)
  }, [formik.values])

  // Ensure we have aivy_form_id before enabling signup when email is prefilled via URL
  useEffect(() => {
    if (context !== 'SIGNUP') return

    // If no prefilled email (manual signup), no need to wait
    if (!isSignupWithPrefilledEmail) {
      setAivyFormIdReady(true)
      return
    }

    let cancelled = false
    let intervalId = null

    const checkReady = () => {
      if (cancelled) return
      try {
        const url = new URL(window.location.href)
        const fromUrl = url.searchParams.get('aivy_form_id')
        const fromStorage = localStorage.getItem('aivyFormId')
        const ready = Boolean(fromUrl || fromStorage)
        setAivyFormIdReady(ready)
        if (ready && intervalId) window.clearInterval(intervalId)
      } catch (e) {
        // ignore
      }
    }

    // initial check and then poll briefly until ready
    checkReady()
    intervalId = window.setInterval(checkReady, 300)

    return () => {
      cancelled = true
      if (intervalId) window.clearInterval(intervalId)
    }
  }, [context, isSignupWithPrefilledEmail])

  useEffect(() => {
    if (context === 'LOGIN') return

    if (localStorage.getItem('signup_email')) {
      setState('ENTER_CODE')
    }
  }, [context])

  const switchToLogin = () => {
    // Store email for login form
    localStorage.setItem('signup_email', formik.values.email.toLowerCase())

    // Redirect to root - this will trigger the normal login flow
    window.location.href = '/'
  }

  const confirmEmail = async () => {
    setError(null)

    const { validateForm, setSubmitting } = formik
    const validation = await validateForm()

    if (!isValid(validation)) return

    const { email } = formik.values

    setSubmitting(true)
    onLoadingChange?.(true)

    if (context === 'SIGNUP') {
      // Registration flow
      try {
        // Create trial partner (handles user registration internally)
        const { query } = await import('../graphql')

        const result = await query({
          query: 'partnerControl',
          variables: {
            action: 'createTrialPartner',
            email: email.toLowerCase(),
            language: i18n.language || 'de',
            href: window.location.href,
            // Prefer URL param, fallback to localStorage for robustness
            aivy_form_id:
              new URL(window.location.href).searchParams.get('aivy_form_id') ||
              localStorage.getItem('aivyFormId') ||
              undefined
          },
          authMode: 'AWS_IAM'
        })

        trackAdsConversion('registrierung')

        // Parse the response to get auth tokens
        const parsedResult = JSON.parse(result)

        const authTokens =
          parsedResult.body.createFirstMemberToPartner.res.body
            .generateAuthTokens.res

        if (authTokens && authTokens.accessToken) {
          // New user registration - proceed with automatic login
          try {
            // Use the authorizeByToken function approach
            const authData = {
              idToken: authTokens.idToken,
              accessToken: authTokens.accessToken,
              refreshToken: authTokens.refreshToken
            }

            const userConfig = {
              userPoolId: Auth._config.userPoolId,
              clientId: Auth._config.userPoolWebClientId
            }

            // Import amazon-cognito-identity-js
            const AmazonCognitoIdentity = await import(
              'amazon-cognito-identity-js'
            )

            // Create CognitoAccessToken using the response accessToken
            const AccessToken = new AmazonCognitoIdentity.CognitoAccessToken({
              AccessToken: authData.accessToken
            })

            // Create CognitoIdToken using the response idToken
            const IdToken = new AmazonCognitoIdentity.CognitoIdToken({
              IdToken: authData.idToken
            })

            // Create RefreshToken using the response refreshToken
            const RefreshToken = new AmazonCognitoIdentity.CognitoRefreshToken({
              RefreshToken: authData.refreshToken
            })

            // Create session object with all the tokens
            const sessionData = {
              IdToken,
              AccessToken,
              RefreshToken
            }

            // Create the CognitoUserSession using the sessionData
            const session = new AmazonCognitoIdentity.CognitoUserSession(
              sessionData
            )

            // Create object with the UserPoolId and ClientId
            const poolData = {
              UserPoolId: userConfig.userPoolId,
              ClientId: userConfig.clientId
            }

            // Pass the poolData object to CognitoUserPool
            const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData)

            // Create object containing the username and user pool
            const idTokenData = JSON.parse(atob(authData.idToken.split('.')[1]))
            const userEmail = idTokenData.email
            const cognitoUsername = idTokenData['cognito:username']

            // IMPORTANT: Use cognito:username for the CognitoUser, but email for localStorage keys
            const userData = {
              Username: cognitoUsername, // Use cognito:username UUID for CognitoUser
              Pool: userPool
            }

            // Create cognito user using the userData object
            const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData)

            // Set the cognito user session with the CognitoUserSession
            cognitoUser.setSignInUserSession(session)
            // CRITICAL: Cache the tokens to localStorage
            cognitoUser.cacheTokens()

            // Set additional required metadata for proper Amplify recognition
            const clientId = Auth._config.userPoolWebClientId

            // IMPORTANT: Set LastAuthUser to the cognito:username (not email)
            localStorage.setItem(
              `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`,
              cognitoUsername
            )

            // Calculate and store clockDrift (required for token validation)
            const accessTokenData = JSON.parse(
              atob(authData.accessToken.split('.')[1])
            )
            const clockDrift = Math.floor(
              (accessTokenData.iat - idTokenData.iat) / 2
            )

            localStorage.setItem(
              `CognitoIdentityServiceProvider.${clientId}.${cognitoUsername}.clockDrift`,
              clockDrift.toString()
            )

            // Store userData (required for proper Amplify functionality)
            const userDataForStorage = {
              UserAttributes: [
                { Name: 'sub', Value: idTokenData.sub },
                { Name: 'email_verified', Value: 'true' },
                { Name: 'email', Value: userEmail }
              ],
              Username: cognitoUsername
            }

            localStorage.setItem(
              `CognitoIdentityServiceProvider.${clientId}.${cognitoUsername}.userData`,
              JSON.stringify(userDataForStorage)
            )

            // Don't set deviceKey and randomPasswordKey - they should not exist for new users
            // These are only set when they have actual values, not as "undefined" strings

            // CRITICAL: Set the user in Amplify's internal cache
            // This ensures that Auth.currentAuthenticatedUser() will find the user
            Auth.user = cognitoUser

            // Set the current user session in Auth
            Auth.userSession = session

            // Reset form
            formik.resetForm()

            // Redirect to discover after successful authentication
            // The useCognito hook will automatically detect the user on page load

            setTimeout(() => {
              window.location.href = '/'
            }, 500)
          } catch (tokenError) {
            console.error('❌ authorizeByToken failed:', tokenError)
            handleError(tokenError)
          }
        } else {
          // No auth tokens - user already exists, show login option
          setError({
            text: 'account_already_exists'
          })
        }
      } catch (err) {
        console.error('Error in createTrialPartner flow:', err)
        handleError(err)
      } finally {
        setSubmitting(false)
        onLoadingChange?.(false)
      }
    } else {
      // Login flow
      onLoadingChange?.(true)
      Auth.configure({ authenticationFlowType: 'CUSTOM_AUTH' })
      Auth.signIn(email.toLowerCase())
        .then(
          (res) =>
            setCognitoUser(res) || setState('ENTER_CODE') || setStartTimer(true)
        )
        .catch((err) => handleError(err))
        .finally(() => {
          setSubmitting(false)
          onLoadingChange?.(false)
        })
    }
  }

  // Auto-fire confirmEmail when /login?email=…&autologin=1 is present and
  // the email is already valid. Runs once. Skips for SIGNUP context (which
  // has its own form-id-ready gate) and once user is already in code-entry.
  useEffect(() => {
    if (!wantsAutoLogin) return
    if (autoLoginFiredRef.current) return
    if (context === 'SIGNUP') return
    if (state !== 'ENTER_EMAIL') return
    if (!formik.values.email) return
    if (!EMAIL_REGEX.test(formik.values.email)) return
    autoLoginFiredRef.current = true
    confirmEmail()
    // confirmEmail is stable in scope; we intentionally only react to readiness flags.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsAutoLogin, context, state, formik.values.email])

  const confirmCode = async (codeValue = null) => {
    const { validateForm, setSubmitting, resetForm } = formik

    if (codeValue === null) {
      const validation = await validateForm()
      if (!isValid(validation)) return
    }

    const code = codeValue !== null ? codeValue : formik.values.code

    setSubmitting(true)
    onLoadingChange?.(true)

    if (context === 'SIGNUP') {
      // Registration confirmation - sign in with passwordless flow
      try {
        Auth.configure({ authenticationFlowType: 'CUSTOM_AUTH' })
        const signInResult = await Auth.signIn(cognitoUser.username)
        await Auth.sendCustomChallengeAnswer(signInResult, code)

        // Get the current session to ensure authentication is complete
        await Auth.currentSession()

        // Trigger the Hub event to notify useCognito hook
        const { Hub } = await import('aws-amplify')
        const authenticatedUser = await Auth.currentAuthenticatedUser()
        Hub.dispatch('auth', {
          event: 'signIn',
          data: authenticatedUser
        })

        resetForm()

        // Experiment onboarding_skip (Rock Q3/26): Variante B startet direkt im
        // Stellen-Flow statt auf der Intro-Seite. Dieser Redirect ist ein voller
        // Page-Reload — der Partner-Context existiert hier noch nicht, also die
        // partner_id direkt aus dem frischen Token lesen (gleiche Quelle wie
        // App.js). Fehlt sie (Token vor dem Partner-Setup ausgestellt), bleibt
        // es beim Status quo — Variante A ist der sichere Fallback.
        const groups =
          authenticatedUser?.signInUserSession?.accessToken?.payload?.[
            'cognito:groups'
          ] || []
        const signupPartnerId = groups
          .find((g) => g.includes('__active_partner_id'))
          ?.split(':')
          .pop()
        const target = skipsOnboarding(signupPartnerId, false)
          ? ONBOARDING_SKIP_ROUTE
          : '/discover/intro'

        // Kontrollmetrik: wie oft ist die partner_id hier noch unbekannt? Wäre
        // das häufig, würde es Variante B systematisch nach A ziehen.
        trackEvent('signup_landing_resolved', {
          partner_id_known: Boolean(signupPartnerId),
          target
        })

        setTimeout(() => {
          window.location.href = target
        }, 500)
      } catch (err) {
        console.error('Registration confirmation error:', err)
        handleError(err)
      } finally {
        setSubmitting(false)
        onLoadingChange?.(false)
      }
    } else {
      // Login confirmation
      Auth.sendCustomChallengeAnswer(cognitoUser, code)
        .then(() => {
          Auth.currentSession()
            .then(() => resetForm())
            .catch((err) => handleError(err))
            .finally(() => {
              setSubmitting(false)
              onLoadingChange?.(false)
            })
        })
        .catch((err) => notifyBugsnag(err))
        .finally(() => setSubmitting(false))
    }
  }

  const isValid = (validation) => {
    const valid = !Object.keys(validation).length

    if (!valid) {
      const fields = {}
      const { setTouched } = formik

      Object.keys(validation).forEach((field) => (fields[field] = true))

      setTouched(fields)
    }

    return valid
  }

  const handleError = (err) => {
    formik.setFieldValue('code', '')

    if (err === 'No current user') {
      setError({ text: 'CodeMismatchException'.toLowerCase() })
      return
    }

    if (
      ['UserNotFoundException', 'NotAuthorizedException'].includes(err.code)
    ) {
      setError({ text: err.code.toLowerCase() })
      return
    }

    setError({ text: 'ERROR'.toLowerCase() })
    notifyBugsnag(err)
  }

  return (
    <form
      style={{ height: fixedHeight ? 512 : 'auto' }}
      onSubmit={formik.handleSubmit}
    >
      {!hideEmailField && (
        <InputFormik
          {...forms.email}
          formik={formik}
          disabled={state === 'ENTER_CODE'}
        />
      )}

      {context === 'LOGIN' && (
        <div
          className={classNames(
            'mt-4',
            (state === 'ENTER_EMAIL' || state === 'CODE_EXPIRED') && 'hidden'
          )}
        >
          <label className='block text-sm font-medium text-gray-700'>
            {forms.code.label}
          </label>
          <div className='otp-code-input mt-2 flex justify-center'>
            <ReactCodeInput
              ref={codeInputRef}
              name='code'
              type='number'
              fields={5}
              value={formik.values.code}
              onChange={(value) => {
                formik.setFieldValue('code', value)
                if (value.length === 5 && !formik.isSubmitting) {
                  confirmCode(value)
                }
              }}
              disabled={formik.isSubmitting}
              inputStyle={{
                MozAppearance: 'textfield',
                WebkitAppearance: 'none',
                boxSizing: 'border-box',
                margin: '0 3px',
                padding: '0',
                width: '46px',
                height: '38px'
              }}
              inputStyleInvalid={{
                MozAppearance: 'textfield',
                WebkitAppearance: 'none',
                boxSizing: 'border-box',
                margin: '0 3px',
                padding: '0',
                width: '46px',
                height: '38px'
              }}
            />
          </div>
          {forms.code.description && (
            <p className='mt-2 text-xs italic text-gray-500'>
              {forms.code.description}
            </p>
          )}
        </div>
      )}

      {context === 'LOGIN' && state === 'CODE_EXPIRED' && (
        <div className='mt-4'>
          <Alert title={t('authenticator.session_expired_login')} />
        </div>
      )}

      {error && (
        <div className='mt-4'>
          <Alert
            title={t(`authenticator.${error.text}_` + context.toLowerCase(), {
              ...error.params
            })}
          />
        </div>
      )}

      <div className='mt-6'>
        {state === 'ENTER_EMAIL' && (
          <>
            <Button
              type='submit'
              appearance='w-full-base'
              disabled={
                formik.isValid === false ||
                (context === 'SIGNUP' &&
                  isSignupWithPrefilledEmail &&
                  !aivyFormIdReady)
              }
              text={
                error?.text === 'account_already_exists'
                  ? t('authenticator.button_login')
                  : error?.text === 'company_domain_exist'
                    ? t(
                        'authenticator.confirm_email_action_signup_domain_exists'
                      )
                    : t(
                        'authenticator.confirm_email_action_' +
                          context.toLowerCase()
                      )
              }
              onClick={
                error?.text === 'account_already_exists'
                  ? switchToLogin
                  : confirmEmail
              }
              isLoading={formik.isSubmitting}
            />
            {context === 'SIGNUP' &&
              isSignupWithPrefilledEmail &&
              !aivyFormIdReady && (
                <p className='mt-2 text-xs text-gray-500'>
                  {t('authenticator.wait_for_aivy_form_id_signup')}
                </p>
              )}
          </>
        )}
        {context === 'LOGIN' && state === 'ENTER_CODE' && (
          <Button
            type='submit'
            appearance='w-full-base'
            text={t(
              'authenticator.confirm_code_action_' + context.toLowerCase()
            )}
            onClick={() => confirmCode()}
            isLoading={formik.isSubmitting}
          />
        )}
        {context === 'LOGIN' && state === 'CODE_EXPIRED' && (
          <Button
            appearance='w-full-base'
            text={t('authenticator.session_expired_action_login')}
            onClick={confirmEmail}
            isLoading={formik.isSubmitting}
          />
        )}
      </div>
    </form>
  )
}

AuthContainer.propTypes = {
  context: PropTypes.string.isRequired,
  signupEmail: PropTypes.string,
  signupSearchParams: PropTypes.string,
  withBorder: PropTypes.bool,
  fixedHeight: PropTypes.bool,
  hideEmailField: PropTypes.bool,
  onLoadingChange: PropTypes.func
}

AuthContainer.defaultProps = {
  signupEmail: undefined,
  signupSearchParams: undefined,
  withBorder: false,
  fixedHeight: false,
  hideEmailField: false,
  onLoadingChange: undefined
}

export default AuthContainer
